"use client";

import { ButtonHTMLAttributes } from "react";
import { Select } from "@dxp-feed/ui/Select";
import { usePagination } from "@dxp-feed/algolia";
import { Box } from "@dxp-feed/ui/Box";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "@/utils/hooks/reactUseCustomHooks";
import {
  GLOBAL_SEARCH_RESULTS_SECTIONS,
  SEARCH_RESULT_TAB_QUERY_PARAM_KEY,
} from "@/utils/constant";
import { GlobalSearchSectionsType } from "@/utils/types/algolia";

interface AlgoliaPaginationProps {
  type: GlobalSearchSectionsType;
  changeItemPerPage: (val: string) => void;
  itemPerPage: number;
}
export default function AlgoliaPagination({
  type,
  changeItemPerPage,
  itemPerPage,
}: Readonly<AlgoliaPaginationProps>) {
  const {
    nbPages: totalSize,
    currentRefinement,
    refine,
    nbHits,
  } = usePagination();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(currentRefinement);
  const pageFromQuery = searchParams.get("page");
  const [shouldFlip, setShouldFlip] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const [itemsPerPage, setItemsPerPage] = useState(itemPerPage);
  const pathname = usePathname();
  const currentTab =
    (searchParams.get(
      SEARCH_RESULT_TAB_QUERY_PARAM_KEY
    ) as GLOBAL_SEARCH_RESULTS_SECTIONS) ??
    GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT;

  const changePage = (value: string) => {
    refine(0);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("itemsPerPage", value);
    newParams.set("page", "1");
    router.push(`?${newParams.toString()}`, { scroll: false });
    changeItemPerPage(value);
    setCurrentPage(0);
  };
  const option = [
    { optionname: "10 / Page", optionval: "10" },
    { optionname: "12 / Page", optionval: "12" },
    { optionname: "15 / Page", optionval: "15" },
    { optionname: "20 / Page", optionval: "20" },
    { optionname: "50 / Page", optionval: "50" },
    { optionname: "100 / Page", optionval: "100" },
  ];

  useEffect(() => {
    setCurrentPage(0);
    refine(0);
  }, [currentTab, refine]);
  useEffect(() => {
    setItemsPerPage(itemPerPage);
    setCurrentPage(currentRefinement);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [itemPerPage, currentRefinement]);

  useEffect(() => {
    const itemsPerPageQuery = searchParams.get("itemsPerPage");
    const newPageParam = new URLSearchParams(searchParams.toString());
    let shouldSkipPageHandling = false;
    if (itemsPerPageQuery) {
      const perPage = parseInt(itemsPerPageQuery, 10);
      if (!isNaN(perPage)) {
        const validOptions = option.map((opt) => parseInt(opt.optionval, 10));
        const isValid = validOptions.includes(perPage);
        let finalPerPage = perPage;
        if (!isValid) {
          // Find nearest valid value
          finalPerPage = validOptions.reduce(
            (prev, curr) =>
              Math.abs(curr - perPage) < Math.abs(prev - perPage) ? curr : prev,
            validOptions[0]
          );
          // Update URL with valid value
          const newParams = new URLSearchParams(searchParams.toString());
          newParams.set("itemsPerPage", `${finalPerPage}`);
          newParams.set("page", "1");
          router.push(`?${newParams.toString()}`, { scroll: false });
          shouldSkipPageHandling = true;
        }
        setItemsPerPage(perPage);
        changeItemPerPage(itemsPerPageQuery);
      }
    }
    if (
      !shouldSkipPageHandling &&
      pageFromQuery &&
      (pathname !== "/" || currentTab === type)
    ) {
      const pageNumber = parseInt(pageFromQuery, 10);
      if (!isNaN(pageNumber) && pageNumber <= totalSize && pageNumber > 0) {
        refine(pageNumber - 1);
        setCurrentPage(pageNumber - 1);
      } else if (!isNaN(pageNumber) && pageNumber < 1) {
        // underflow condition
        newPageParam.set("page", "1");
        router.push(`?${newPageParam.toString()}`, { scroll: false });
        refine(0);
        setCurrentPage(1);
      } else if (
        !isNaN(pageNumber) &&
        pageNumber > totalSize &&
        totalSize != 0 &&
        pathname !== "/"
      ) {
        // Overflow condition
        newPageParam.set("page", `${totalSize}`);
        router.push(`?${newPageParam.toString()}`, { scroll: false });
        refine(totalSize - 1);
        setCurrentPage(totalSize - 1);
      }
    }
  }, [searchParams, refine, totalSize]);

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("page", (page + 1).toString());
    router.push(`?${newParams.toString()}`, { scroll: false });

    setCurrentPage(page);
    refine(page);
  };

  // Flip if there's not enough space below but enough space above
  const checkDropdownPosition = () => {
    if (selectRef.current) {
      // Return position and size of the element relative to the viewport.
      const rect = selectRef.current.getBoundingClientRect();
      //  Distance between the bottom of <Select> and the bottom of the viewport
      const spaceBelow = window.innerHeight - rect.bottom;
      // Distance between the top of <Select> and the top of the viewport.
      const spaceAbove = rect.top;
      //If there is less than 200px of space below and at least 200px of space above, then flip the dropdown to open above
      setShouldFlip(spaceBelow < 200 && spaceAbove > 200);
    }
  };

  return (
    <>
      {nbHits ? (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 justify-center items-center w-[100%] mt-6">
          <div className="flex items-center gap-[10px] md:gap-[20px]">
            <PageButton
              aria-label="Previous Arrow"
              className="hover:cursor-pointer paginationArrow"
              label={`icon-prev-arrow text-[12px] text-brandSecondary group-hover:text-brandWhite ${currentPage == 0 ? "!text-[#818181]" : "text-brandSecondary"}`}
              disabled={currentPage == 0}
              onClick={() => handlePageChange(currentPage - 1)}
            />
            <ul className="flex gap-6">
              <PaginationCustom
                pageNum={totalSize}
                onPageChange={handlePageChange}
                currentPage={currentPage + 1}
              />
            </ul>
            <PageButton
              aria-label="Next Arrow"
              className="hover:cursor-pointer paginationArrow"
              label={`icon-next-arrow text-[12px] text-brandSecondary group-hover:text-brandWhite ${currentPage + 1 == totalSize ? "!text-[#818181]" : "text-brandSecondary"}`}
              disabled={currentPage + 1 == totalSize}
              onClick={() => handlePageChange(currentPage + 1)}
            />
          </div>

          <Select
            ref={selectRef}
            selectAriaLabel={` ${itemsPerPage} / page `}
            options={option}
            defaultValue={`${itemsPerPage}`}
            onChange={changePage}
            onMouseDown={checkDropdownPosition}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                checkDropdownPosition();
              }
            }}
            className={`tab relative ${shouldFlip ? "[&_ul]:bottom-full [&_ul]:top-auto" : ""} [&_button]:rounded-[4px] [&_button]:text-brandBlack [&_span:last-child]:right-[14px] [&_button]:py-[4.5px] [&_button]:pl-[8px] [&_button]:pr-[36px] [&_li]:py-[4.5px] [&_li]:text-[14px]`}
          />
        </div>
      ) : null}
    </>
  );
}

const PaginationCustom = (props: any) => {
  const { currentPage, numberOfPagesNextToActivePage = 1, pageNum } = props;
  const current = currentPage,
    last = pageNum,
    delta = numberOfPagesNextToActivePage,
    left = current - delta,
    right = current + delta + 1,
    range = [],
    rangeWithEllipsis: JSX.Element[] = [];
  let l = undefined;

  for (let i = 1; i <= last; i++) {
    if (i === 1 || i === last || (i >= left && i < right)) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (l && i - l !== 1) {
      rangeWithEllipsis.push(
        <li key={`ellipsis-${i}`} className="pageElli list-none">
          <span className="text-[12px]">...</span>
        </li>
      );
    }
    rangeWithEllipsis.push(
      <li
        key={i}
        className="ais-Pagination-item flex items-center justify-center list-none"
      >
        <button
          className="cursor-pointer text-[12px] ais-Pagination-item--page ais-Pagination-item--selected [&[data-state=true]]:font-bold [&[data-state=true]]:text-brandBlack text-[black] disabled:cursor-not-allowed"
          data-state={currentPage === i}
          onClick={(e) => {
            e.preventDefault();
            props.onPageChange(i - 1);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              props.onPageChange(i - 1);
            }
          }}
          aria-label={
            currentPage === i ? `Current Page ${i}` : `Go to page ${i}`
          }
        >
          {i}
        </button>
      </li>
    );
    l = i;
  }

  return <>{rangeWithEllipsis}</>;
};

const PageButton = (
  props: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }
) => {
  const { label, disabled, ...rest } = props;
  return (
    <Box as="div" className="py-1 px-2">
      <button
        {...rest}
        disabled={disabled}
        aria-label={label}
        className={`border border-brandSecondary w-10 h-10 px-4 py-2 rounded-[200px] flex items-center justify-center p-20px ${disabled ? "cursor-not-allowed !border-[#e3e3e3]" : "cursor-pointer opacity-100 hover:bg-brandSecondary hover:text-brandWhite"}`}
      >
        <span
          className={`${label} ${disabled ? "text-grey600" : "text-inherit"}`}
        ></span>
      </button>
    </Box>
  );
};
